import { useState } from "react";
import { PackageMinus, Plus, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Empty, PageHeader, Tone } from "@/components/shared";
import { RmaDetail } from "@/components/RmaDetail";
import { api, errMsg, fdate, label, useApi } from "@/lib/api";

const RMA_STATUSES = ["OPEN", "SENT_TO_VENDOR", "REPLACEMENT_RECEIVED", "CLOSED", "REJECTED"];
const TONE = { OPEN: "blue", SENT_TO_VENDOR: "purple", REPLACEMENT_RECEIVED: "green", CLOSED: "gray", REJECTED: "red" };

function NewRma({ open, onClose, onCreated }) {
  const { data: products } = useApi("/inventory");
  const { data: tickets } = useApi("/tickets");
  const empty = { sourceType: "INVENTORY_DEFECTIVE", productId: "", quantityDeducted: 1, partName: "", serialNumber: "", projectCode: "", customerName: "", ticketId: "", issueDescription: "", vendorRmaRef: "" };
  const [f, setF] = useState(empty);
  const inv = f.sourceType === "INVENTORY_DEFECTIVE";
  const prod = products?.find((p) => p.id === f.productId);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const r = await api.post("/rma", { ...f, quantityDeducted: +f.quantityDeducted, productId: f.productId || null, ticketId: f.ticketId || null });
      toast.success(`${r.data.rmaNumber} created${inv ? ` — ${r.data.quantityDeducted} unit(s) deducted from stock` : ""}`);
      setF(empty);
      onCreated();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="new-rma-dialog">
        <DialogHeader><DialogTitle>New RMA</DialogTitle></DialogHeader>
        <RadioGroup value={f.sourceType} onValueChange={(v) => setF({ ...empty, sourceType: v })} className="grid grid-cols-2 gap-3">
          {[["INVENTORY_DEFECTIVE", "Inventory Product", "Defective in stock", PackageMinus], ["PREVIOUSLY_SOLD_CUSTOMER", "Previously Sold Product", "Customer warranty claim", ShieldAlert]].map(([v, t, d, I]) => (
            <label key={v} className={`flex gap-3 rounded-lg border p-3 cursor-pointer ${f.sourceType === v ? "border-primary bg-primary/5" : ""}`}>
              <RadioGroupItem value={v} className="mt-1" data-testid={`rma-source-${v.toLowerCase()}`} />
              <div><div className="text-sm font-medium flex items-center gap-1.5"><I className="h-4 w-4" />{t}</div><div className="text-xs text-muted-foreground">{d}</div></div>
            </label>
          ))}
        </RadioGroup>
        <div className="grid grid-cols-2 gap-4">
          {inv ? (
            <>
              <div className="col-span-2 space-y-1.5"><Label>Product *</Label>
                <Select value={f.productId} onValueChange={(v) => setF({ ...f, productId: v })}>
                  <SelectTrigger data-testid="rma-product-select"><SelectValue placeholder="Select inventory product" /></SelectTrigger>
                  <SelectContent>{products?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.stockQuantity} in stock)</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Quantity to deduct *</Label><Input type="number" min={1} value={f.quantityDeducted} onChange={set("quantityDeducted")} data-testid="rma-qty-input" /></div>
              <div className="space-y-1.5"><Label>Serial number</Label><Input value={f.serialNumber} onChange={set("serialNumber")} data-testid="rma-serial-input" /></div>
              {prod && <div className="col-span-2 rounded-md bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-400" data-testid="rma-deduct-notice">
                Stock will be reduced from {prod.stockQuantity} to {prod.stockQuantity - (+f.quantityDeducted || 0)} and logged as "RMA Defect Deduct".</div>}
            </>
          ) : (
            <>
              <div className="space-y-1.5"><Label>Original project code</Label><Input value={f.projectCode} onChange={set("projectCode")} placeholder="PRJ-1001" data-testid="rma-project-input" /></div>
              <div className="space-y-1.5"><Label>Customer name</Label><Input value={f.customerName} onChange={set("customerName")} data-testid="rma-customer-input" /></div>
              <div className="space-y-1.5"><Label>Part name *</Label><Input value={f.partName} onChange={set("partName")} data-testid="rma-part-input" /></div>
              <div className="space-y-1.5"><Label>Serial number *</Label><Input value={f.serialNumber} onChange={set("serialNumber")} data-testid="rma-serial-input" /></div>
              <div className="col-span-2 space-y-1.5"><Label>Linked ticket</Label>
                <Select value={f.ticketId} onValueChange={(v) => setF({ ...f, ticketId: v })}>
                  <SelectTrigger data-testid="rma-ticket-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>{tickets?.map((t) => <SelectItem key={t.id} value={t.id}>{t.ticketNumber} · {t.subject}</SelectItem>)}</SelectContent>
                </Select></div>
            </>
          )}
          <div className="col-span-2 space-y-1.5"><Label>Issue description *</Label><Textarea rows={2} value={f.issueDescription} onChange={set("issueDescription")} data-testid="rma-issue-input" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Vendor RMA reference</Label><Input value={f.vendorRmaRef} onChange={set("vendorRmaRef")} data-testid="rma-vendor-ref-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.issueDescription} data-testid="create-rma-button">Create RMA</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Rma() {
  const { data, reload, setData } = useApi("/rma");
  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const setStatus = async (r, status) => {
    const put = (v) => setData((list) => list.map((x) => (x.id === r.id ? v : x)));
    put({ ...r, status });
    try { put((await api.patch(`/rma/${r.id}`, { status })).data); toast.success("RMA updated"); }
    catch (e) { put(r); toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="RMA Registry" subtitle="Defective stock and customer warranty claims with vendor replacement tracking.">
        <Button onClick={() => setOpen(true)} data-testid="new-rma-button"><Plus className="h-4 w-4 mr-2" />New RMA</Button>
      </PageHeader>
      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="rma-table">
          <thead><tr className="border-b bg-muted/40 text-xs text-muted-foreground">
            {["RMA", "Source", "Part", "Serial", "Qty deducted", "Issue", "Vendor ref", "Created", "Status"].map((h) => <th key={h} className="text-left font-medium px-4 py-2.5">{h}</th>)}
          </tr></thead>
          <tbody>{(data || []).map((r) => (
            <tr key={r.id} className="border-b last:border-0 align-top" data-testid={`rma-row-${r.rmaNumber}`}>
              <td className="px-4 py-3"><button onClick={() => setOpenId(r.id)} className="font-mono text-xs font-semibold text-primary hover:underline" data-testid={`open-rma-${r.rmaNumber}`}>{r.rmaNumber}</button>
                <div className="text-[10px] text-muted-foreground">{(r.comments || []).length} notes · {(r.conditionMedia || []).length} media</div></td>
              <td className="px-4 py-3">{r.sourceType === "INVENTORY_DEFECTIVE" ? <Tone tone="amber">Inventory defect</Tone> : <Tone tone="indigo">Customer claim</Tone>}
                {r.projectCode && <div className="text-[11px] text-muted-foreground mt-1">{r.projectCode} {r.customerName && `· ${r.customerName}`}</div>}</td>
              <td className="px-4 py-3">{r.partName}</td><td className="px-4 py-3 font-mono text-xs">{r.serialNumber || "—"}</td>
              <td className="px-4 py-3 num">{r.quantityDeducted}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs">{r.issueDescription}</td>
              <td className="px-4 py-3 font-mono text-xs">{r.vendorRmaRef || "—"}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fdate(r.createdAt)}</td>
              <td className="px-4 py-3">
                <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                  <SelectTrigger className="h-8 w-48 text-xs" data-testid={`rma-status-${r.rmaNumber}`}><Tone tone={TONE[r.status]}>{label(r.status)}</Tone></SelectTrigger>
                  <SelectContent>{RMA_STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
                </Select>
              </td>
            </tr>
          ))}</tbody>
        </table>
        {data?.length === 0 && <Empty text="No RMA records" />}
      </div>
      <RmaDetail rma={(data || []).find((x) => x.id === openId)} onClose={() => setOpenId(null)} onUpdate={(x) => setData(data.map((y) => (y.id === x.id ? x : y)))} />
      <NewRma open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); reload(); }} />
    </div>
  );
}
